import * as PropTypes from "prop-types";

import { promisify } from "../../../utilities";
import { useState } from "react";

import * as piasu from "../NonComponents/PIAppStateUtil";
import * as gbu from "../../GB/GBUtil";
import * as gbtu from "../../GB/GBTableUtil";
import * as pisc from "../NonComponents/PIServerConst";
import * as gbtc from "../../GB/GBTableConst";
import * as pitu from "../NonComponents/PITableUtil";

import { RS } from "../../../data/strings/global";
import * as SC from "../../../data/strings/PIStringConst";

import * as Theme from "../../../app/Theme";

import { generateTypes } from "../../../utilities";
import SuperTableShim from "../../common/SuperTableShim";
import TButton from "../../common/TButton";

import { combinePriorPops, disaggregatePriorPops, methodMixCombinedPops } from "../NonComponents/PIPriorPopUtil";

const PIRestartRateTable = (props) => {
  const onModVarsChange = promisify(props.onModVarsChange);

  const modVarObjList = props.modVarObjList;
  const isPSEMode = piasu.isPSEMode(modVarObjList);

  /** @type {any[]} */
  const methods = piasu.getModVarValue(modVarObjList, "PI_Methods");
  /** @type {any[]} */
  const allPops = piasu.getModVarValue(modVarObjList, "PI_PriorityPop");
  let priorPops = allPops;
  /** @type {((number|null)[][])|null} */
  let restartRates = piasu.getModVarValue(modVarObjList, "PI_RestartRate");
  /** @type {number[][]} */
  let defaultRestartRates = piasu.getModVarValue(modVarObjList, "PI_DefaultRestartRate");
  let eligibility = piasu.getModVarValue(modVarObjList, "PI_Eligibility");

  if (isPSEMode) {
    const combined = combinePriorPops(modVarObjList, methodMixCombinedPops, [
      "PI_RestartRate",
      "PI_DefaultRestartRate",
      "PI_Eligibility",
    ]);
    restartRates = combined["PI_RestartRate"];
    priorPops = combined["PI_PriorityPop"];
    defaultRestartRates = combined["PI_DefaultRestartRate"];
    eligibility = combined["PI_Eligibility"];
  }

  const gainsboroBase10 = gbu.toBase10(gbu.getDelphiHexFromHexColor(Theme.whisperGrayTableColor));

  const numRows = priorPops.length + 1;
  const numCols = methods.length + 1;

  // State
  const [rDec, setRDecs] = useState([]);

  // Callbacks
  const onPackTableChange = async (newPackTable) => {
    setRDecs(structuredClone(newPackTable.RDec));

    if (props.defaultsOnly) return;

    let newRestartRates = methods.map((_, m) =>
      priorPops.map((_, pp) => {
        const value = gbtu.getValue(newPackTable, pp + 1, m + 1) / 100.0;
        return gbu.equal(value, defaultRestartRates[m][pp], 0.001) ? null : value;
      })
    );

    const newModVars = structuredClone(modVarObjList);
    if (isPSEMode && newRestartRates !== null) {
      const newData = {
        PI_PriorityPop: priorPops,
        PI_RestartRate: newRestartRates,
      };

      const disagg = disaggregatePriorPops(modVarObjList, newData, methodMixCombinedPops);
      newRestartRates = disagg.PI_RestartRate;
    }

    piasu.setModVarValue(newModVars, "PI_RestartRate", newRestartRates);

    await onModVarsChange(newModVars, false);
  };

  const onResetClick = async () => {
    const newModVars = structuredClone(modVarObjList);
    piasu.setModVarValue(newModVars, "PI_RestartRate", null);

    await onModVarsChange(newModVars, false);
  };

  // Table
  const packTable = gbtu.resizePackTable(gbtu.getNewPackTable(), numRows, numCols);

  // Headings
  gbtu.setValue(packTable, 0, 0, RS(SC.GB_stPriorityPop));
  for (let m = 0; m < methods.length; ++m) {
    gbtu.setValue(packTable, 0, m + 1, methods[m].name);
  }

  // Data
  for (let pp = 0; pp < priorPops.length; ++pp) {
    const pop = priorPops[pp];
    gbtu.setValue(packTable, pp + 1, 0, pop.name);

    // Methods
    for (let m = 0; m < methods.length; ++m) {
      const method = methods[m];

      if (piasu.getPriorPopMethodElig(eligibility, method.mstID, pp + 1) === pisc.yesMstID) {
        // If the restart rates array is null or the value at the index is null, use the default restart rate
        const value =
          (!props.defaultsOnly ? restartRates?.[m][pp] ?? defaultRestartRates[m][pp] : defaultRestartRates[m][pp]) *
          100;
        gbtu.setValue(packTable, pp + 1, m + 1, value);
      } else {
        gbtu.lockCell(packTable, pp + 1, m + 1, true, true);
        gbtu.setCellBGColor(packTable, pp + 1, m + 1, gainsboroBase10);
      }
    }
  }

  // Settings
  gbtu.alignNumericCellsRight(packTable);
  gbtu.setRowAlignment(packTable, 0, gbtc.hAlign.center);
  // We have to allow negative numbers otherwise you can't edit any cells if there's more than one with an invalid value
  gbtu.setMinAllowedValForTable(packTable, -100);
  gbtu.setMaxAllowedValForTable(packTable, 100);
  gbtu.setColWidth(packTable, 0, Theme.itemNameColWidthWide);
  gbtu.setRowHeight(packTable, 0, 50);
  gbtu.setWordWrappedCol(packTable, 0, true);
  // gbtu.setID(packTable, "optionsPackTable");
  // gbtu.setKey(packTable, "optionsPackTable");

  if (props.defaultsOnly) {
    gbtu.lockPackTable(packTable, true, false);
  }

  gbtu.setRDecs(packTable, 2);
  if (rDec.length !== 0) {
    for (let r = 0; r < rDec.length; r++) {
      for (let c = 0; c < rDec[r].length; c++) {
        gbtu.setRDec(packTable, r, c, rDec[r][c]);
      }
    }
  }

  return (
    <div>
      <SuperTableShim
        // focusedCell={state[pip.focusedCell]}
        // onCellFocused={(focusedCell) => pitu.onCellFocused(this, focusedCell)}
        // selectedRegions={state[pip.selectedRegions]}
        // onSelectionChanged={(selectedRegions) => pitu.onSelectionChanged(this, selectedRegions)}
        font={Theme.tableFont}
        headerBackgroundColor={Theme.PI_PrimaryColor}
        gridKey={props.tableKey}
        oddRowBackgroundColor={Theme.PI_BandColor}
        packTable={packTable}
        types={generateTypes(packTable)}
        onPackTableChanged={onPackTableChange}
        removedMenuNames={pitu.tableHideMenuItems}
        style={{
          fontFamily: Theme.tableFont,
          marginTop: Theme.ctrlSpacing,
          padding: 0,
        }}
        undoDisabled={false}
      />
      {!props.defaultsOnly && (
        <TButton
          caption={RS(SC.GB_stResetToDefault)}
          key={"defaultRatesBtn"}
          onClick={onResetClick}
          containerStyle={{
            display: "inline-block",
            marginRight: 10,
            marginTop: 2,
          }}
          style={{
            backgroundColor: Theme.PI_TertiaryColor,
            padding: 0,
          }}
        />
      )}
    </div>
  );
};

PIRestartRateTable.propTypes = {
  modVarObjList: PropTypes.array.isRequired,
  onModVarsChange: PropTypes.func,
  tableKey: PropTypes.string,
  defaultsOnly: PropTypes.bool,
};

PIRestartRateTable.defaultProps = {
  onModVarsChange: () => {},
  tableKey: "",
  defaultsOnly: false,
};

export default PIRestartRateTable;
